import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { RefreshCw, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({});

const actionLabels = {
  client_created: "Client créé",
  client_deleted: "Client supprimé",
  client_access_disabled: "Accès client désactivé",
  temporary_password_generated: "Mot de passe temporaire généré",
  access_email_sent: "Email d’accès envoyé",
  subscription_created: "Abonnement créé",
  subscription_updated: "Abonnement modifié",
  subscription_deleted: "Abonnement supprimé",
  subscription_renewed: "Abonnement renouvelé",
  ticket_updated: "Ticket modifié",
  announcement_created: "Annonce créée",
  attachment_request_updated: "Rattachement traité",
  setting_updated: "Paramètre modifié",
};

const entityLabels = {
  client: "Client",
  subscription: "Abonnement",
  ticket: "Ticket",
  announcement: "Annonce",
  subscription_attachment_request: "Rattachement",
  setting: "Paramètre",
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (!Number.isNaN(date.getTime())) {
    return new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeStyle: "short" }).format(date);
  }
  return value;
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("");
  const [entityFilter, setEntityFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (actionFilter) params.set("action", actionFilter);
    if (entityFilter) params.set("entity_type", entityFilter);
    const { data } = await axios.get(`${API}/audit-logs${params.toString() ? `?${params.toString()}` : ""}`, { headers: authHeaders() });
    setLogs(data);
    setLoading(false);
  }, [actionFilter, entityFilter]);

  useEffect(() => { load(); }, [load]);

  const actionOptions = useMemo(() => Object.entries(actionLabels), []);
  const entityOptions = useMemo(() => Object.entries(entityLabels), []);

  return (
    <section data-testid="audit-page">
      <div className="page-header" data-testid="audit-page-header">
        <div>
          <p className="eyebrow" data-testid="audit-page-eyebrow">Sécurité & conformité</p>
          <h1 className="page-title" data-testid="audit-page-title">Journal d’audit</h1>
          <p className="page-subtitle" data-testid="audit-page-subtitle">Trace des actions sensibles Admin/Support.</p>
        </div>
        <button className="btn-secondary flex items-center gap-2" onClick={load} data-testid="audit-refresh-button"><RefreshCw size={16} /> Actualiser</button>
      </div>

      <div className="surface panel-body mb-5" data-testid="audit-filters-panel">
        <div className="ticket-filter-bar" data-testid="audit-filter-bar">
          <select className="field" value={actionFilter} onChange={(event) => setActionFilter(event.target.value)} data-testid="audit-action-filter">
            <option value="">Toutes les actions</option>
            {actionOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <select className="field" value={entityFilter} onChange={(event) => setEntityFilter(event.target.value)} data-testid="audit-entity-filter">
            <option value="">Toutes les entités</option>
            {entityOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <button className="btn-ghost px-3" onClick={() => { setActionFilter(""); setEntityFilter(""); }} data-testid="audit-reset-filters-button">Réinitialiser</button>
        </div>
      </div>

      <div className="surface" data-testid="audit-table-panel">
        <div className="panel-header"><h2 className="font-[Outfit] text-xl text-white" data-testid="audit-table-title">Événements récents</h2><span className="badge badge-neutral" data-testid="audit-count-badge">{logs.length} événement{logs.length > 1 ? "s" : ""}</span></div>
        <div className="table-wrap" data-testid="audit-table-wrap">
          <table className="data-table" data-testid="audit-table">
            <thead><tr><th>Date</th><th>Acteur</th><th>Rôle</th><th>Action</th><th>Entité</th><th>Résumé</th></tr></thead>
            <tbody>
              {loading ? <tr data-testid="audit-loading-row"><td colSpan={6}>Chargement…</td></tr> : logs.length === 0 ? <tr data-testid="audit-empty-row"><td colSpan={6}><div className="empty-state" data-testid="audit-empty-state"><ShieldCheck className="text-[#d4af37]" /><p className="font-[Outfit] text-lg text-white">Aucun événement</p><p className="mt-1 text-sm text-slate-400">Aucune action ne correspond aux filtres sélectionnés.</p></div></td></tr> : logs.map((log) => (
                <tr key={log.id} data-testid={`audit-row-${log.id}`}>
                  <td data-testid={`audit-date-${log.id}`}>{formatDateTime(log.created_at)}</td>
                  <td data-testid={`audit-actor-${log.id}`}>{log.actor_name}</td>
                  <td data-testid={`audit-role-${log.id}`}><span className="badge badge-neutral">{log.actor_role}</span></td>
                  <td data-testid={`audit-action-${log.id}`}>{actionLabels[log.action] || log.action}</td>
                  <td data-testid={`audit-entity-${log.id}`}>{entityLabels[log.entity_type] || log.entity_type}</td>
                  <td data-testid={`audit-summary-${log.id}`}>{log.summary}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}